
import { Component, Vue } from 'vue-property-decorator'
import AiService from '@/services/ai-service'
import AiChatLogService from '@/services/ai-chat-log-service'
import {
    IChatLogItem,
    IChatLogDetail,
    IChatLogListParams,
    IAvailableProvider
} from '@/types/ai-types'

@Component({
    name: 'AiChatLogList'
})
export default class extends Vue {
    private list: IChatLogItem[] = []
    private pageIndex = 1
    private pageSize = 20
    private pageTotal = 0
    private loading = true
    private providerOptions: IAvailableProvider[] = []
    private queryParams = {
        providerId: undefined as number | undefined,
        status: undefined as number | undefined,
        operatorName: ''
    }
    private timeRange: Date[] = []

    // 详情
    private detailVisible = false
    private detailLoading = false
    private detail: IChatLogDetail | null = null

    private mounted() {
        this.getProviders()
        this.getList()
    }

    /** 模型下拉：调 provider/page 拿启用模型（size 取大，避免分页截断下拉选项） */
    private async getProviders() {
        const result = await AiService.listProvidersPage({ page: 1, size: 1000, status: 1 })
        if (result.status === 200 && result.data && result.data.results) {
            this.providerOptions = result.data.results
        }
    }

    /** 组装查询参数：空值不带，驼峰透传（新规范） */
    private buildParams(): IChatLogListParams {
        const params: IChatLogListParams = {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize
        }
        if (this.queryParams.providerId !== undefined && this.queryParams.providerId !== null) {
            params.providerId = this.queryParams.providerId
        }
        if (this.queryParams.status !== undefined && this.queryParams.status !== null) {
            params.status = this.queryParams.status
        }
        if (this.queryParams.operatorName) params.operatorName = this.queryParams.operatorName
        if (this.timeRange && this.timeRange.length === 2) {
            params.startTime = this.formatDateTime(this.timeRange[0])
            params.endTime = this.formatDateTime(this.timeRange[1])
        }
        return params
    }

    private async getList() {
        this.loading = true
        const result = await AiChatLogService.listChatLogs(this.buildParams())
        this.loading = false
        if (result.status === 200 && result.data) {
            this.list = result.data.list || []
            this.pageTotal = result.data.page_total || 0
        }
    }

    private onSearch() {
        this.pageIndex = 1
        this.getList()
    }

    private onReset() {
        this.queryParams = {
            providerId: undefined,
            status: undefined,
            operatorName: ''
        }
        this.timeRange = []
        this.pageIndex = 1
        this.getList()
    }

    private onCurrentChange(page: number) {
        this.pageIndex = page
        this.getList()
    }

    private async onViewDetail(id: number) {
        this.detailVisible = true
        this.detailLoading = true
        this.detail = null
        const result = await AiChatLogService.getChatLog(id)
        this.detailLoading = false
        if (result.status === 200 && result.data) {
            this.detail = result.data
        }
    }

    /** Date/ISO 字符串 → 'yyyy-MM-dd HH:mm:ss' */
    private formatDateTime(input: string | Date): string {
        if (!input) return '—'
        const d = input instanceof Date ? input : new Date(input)
        if (isNaN(d.getTime())) return String(input)
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n)
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
            ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
    }

    /** 结构化 JSON 字符串 → 对象；解析失败返回 null（模板降级为纯文本展示） */
    private get responseStructObj(): any {
        return this.parseJson(this.detail && this.detail.responseStruct)
    }

    /** 原始返回：若是 JSON 字符串则解析为对象，否则返回 null（模板降级为纯文本） */
    private get responseRawObj(): any {
        return this.parseJson(this.detail && this.detail.responseRaw)
    }

    /** 尝试解析 JSON 字符串，失败返回 null */
    private parseJson(input: string | null | undefined): any {
        if (!input) return null
        try {
            return JSON.parse(input)
        } catch (e) {
            return null
        }
    }
}
